import clsx from "clsx";
import React from "react";
import { Box, BoxProps, Typography, IconButton } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";

import { ReactComponent as ChevronArrowForward } from "../../icons/ChevronForwardOutline.svg";
import {
  getISODate,
  getCalendarMonths,
  parseISODate,
  monthsLabels,
} from "../../utils/date";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    alignItems: "center",
  },

  text: {
    fontSize: 24,
    fontWeight: 600,
  },

  button: {
    color: theme.palette.text.secondary,
    padding: theme.spacing(0.5),
    margin: theme.spacing(0, 1),
  },

  reverse: {
    "& svg": {
      transform: "rotate(180deg)",
    },
  },
}));

export enum MonthSelectButtonsPosition {
  LEFT = "left",
  RIGHT = "right",
  BOTH = "both",
}

export interface MonthSelectProps extends Omit<BoxProps, "onSelect"> {
  onSelect?: (date: string) => void;
  date?: string;
  classes?: {
    container?: string;
    navigationButtons?: string;
    label?: string;
  };
  buttonsPosition?: MonthSelectButtonsPosition;
}

export function MonthSelect(props: MonthSelectProps) {
  const {
    classes,
    date = getISODate(),
    onSelect,
    buttonsPosition = MonthSelectButtonsPosition.BOTH,
    ...other
  } = props;
  const s = useStyles();
  const months = getCalendarMonths(date, -2, 3);
  const [year, month] = parseISODate(date);

  const handleClick = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      const {
        dataset: { date },
      } = event.currentTarget;
      if (date && onSelect) onSelect(date);
    },
    [onSelect],
  );

  const leftButton = (
    <IconButton
      className={clsx(s.button, s.reverse, classes?.navigationButtons)}
      onClick={handleClick}
      data-date={months[0]}
      size="large"
    >
      <ChevronArrowForward />
    </IconButton>
  );

  const rightButton = (
    <IconButton
      className={clsx(s.button, classes?.navigationButtons)}
      onClick={handleClick}
      data-date={months[2]}
      size="large"
    >
      <ChevronArrowForward />
    </IconButton>
  );

  const monthLabel = (
    <Typography className={clsx(s.text, classes?.label)} sx={{ mx: 1 }}>
      {monthsLabels[month]} {year}
    </Typography>
  );

  return (
    <Box
      className={clsx(s.root, classes?.container)}
      {...other}
      sx={{ display: "flex", alignItems: "center" }}
    >
      {buttonsPosition === MonthSelectButtonsPosition.BOTH && leftButton}
      {buttonsPosition === MonthSelectButtonsPosition.LEFT && (
        <Box sx={{ display: "flex", gap: 1 }}>
          {leftButton}
          {rightButton}
        </Box>
      )}

      {monthLabel}

      {buttonsPosition === MonthSelectButtonsPosition.BOTH && rightButton}
      {buttonsPosition === MonthSelectButtonsPosition.RIGHT && (
        <Box sx={{ display: "flex", gap: 1 }}>
          {leftButton}
          {rightButton}
        </Box>
      )}
    </Box>
  );
}
